import React, { useState } from "react";
import {
  isValidAccessCodeFormat,
  normalizeAccessCode,
} from "../../../utils/self.serve.access-code";
import { EMAIL_MAX_LENGTH, isEmailShape } from "../../../utils/self.serve.email";

/** Props for {@link ClassLandingAccessCodeForm}. */
interface ClassLandingAccessCodeFormProps {
  /** Disables the inputs and button while a redeem request is in flight. */
  submitting: boolean;
  /** Called with the trimmed email and normalized code once both pass validation. */
  onSubmit: (email: string, code: string) => void;
}

/**
 * "Enter access code" tab: email + access code with client-side checks (required fields,
 * email shape, `XXXX-XXXX-XXXX` format). The code is normalized as the student types.
 * Server-side outcomes (name dialog, replace dialog, API errors) are handled by the parent;
 * API errors appear only in its snackbar, never under these inputs.
 */
const ClassLandingAccessCodeForm: React.FC<ClassLandingAccessCodeFormProps> = ({
  submitting,
  onSubmit,
}) => {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [emailError, setEmailError] = useState("");
  const [codeError, setCodeError] = useState("");

  /** Validates both fields, shows field errors, and calls `onSubmit` only when both pass. */
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedEmail = email.trim();
    let valid = true;
    if (!trimmedEmail) {
      setEmailError("Email is required.");
      valid = false;
    } else if (!isEmailShape(trimmedEmail)) {
      setEmailError("Enter a valid email address.");
      valid = false;
    } else {
      setEmailError("");
    }
    if (!code) {
      setCodeError("Access code is required.");
      valid = false;
    } else if (!isValidAccessCodeFormat(code)) {
      setCodeError("Invalid format. Only letters and numbers are allowed (XXXX-XXXX-XXXX).");
      valid = false;
    } else {
      setCodeError("");
    }
    if (!valid) {
      return;
    }
    onSubmit(trimmedEmail, code);
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <p className="text-muted" style={{ fontSize: 14 }}>
        Enter the access code from your confirmation email or bookstore to open
        your simulations.
      </p>
      <div className="form-group">
        <label className="required" htmlFor="ss-redeem-email">Email</label>
        <input
          id="ss-redeem-email"
          type="email"
          maxLength={EMAIL_MAX_LENGTH}
          className={`form-control ${emailError ? "is-invalid" : ""}`}
          autoComplete="email"
          placeholder="Email Address"
          value={email}
          disabled={submitting}
          aria-required="true"
          aria-invalid={Boolean(emailError)}
          aria-describedby={emailError ? "ss-redeem-email-error" : undefined}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />
        {emailError ? (
          <div id="ss-redeem-email-error" className="invalid-feedback">
            {emailError}
          </div>
        ) : null}
      </div>
      <div className="form-group">
        <label className="required" htmlFor="ss-redeem-code">Access code</label>
        <input
          id="ss-redeem-code"
          type="text"
          className={`form-control ${codeError ? "is-invalid" : ""}`}
          autoComplete="off"
          value={code}
          disabled={submitting}
          aria-required="true"
          aria-invalid={Boolean(codeError)}
          aria-describedby={codeError ? "ss-redeem-code-error" : undefined}
          onChange={(event) => {
            setCode(normalizeAccessCode(event.target.value));
          }}
          placeholder="XXXX-XXXX-XXXX"
        />
        {codeError ? (
          <div id="ss-redeem-code-error" className="invalid-feedback">
            {codeError}
          </div>
        ) : null}
      </div>
      <div className="ss-public-actions">
        <button
          type="submit"
          className="edsimsV2-pill-button edsimsV2-pill-button--primary"
          disabled={submitting}
        >
          {submitting ? "Checking…" : "Continue"}
        </button>
      </div>
    </form>
  );
};

export default ClassLandingAccessCodeForm;
